import { EmptyState } from '@/components/patterns/empty-state'
import { LoadingState } from '@/components/patterns/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import {
  useFsnNetworkSalesOrderFlorists,
  useSubmitFsnNetworkSalesOrderMutation,
} from '@/lib/services/fsn-network/fsn-network.query'
import { parseOptionalFsnMoney } from '@/lib/services/fsn-network/fsn-network.utils'
import { cn, formatCurrency, getErrorMessage } from '@/lib/utils'
import { AlertTriangle, Ban, Loader2, Phone, Search, Star } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import type { CountryCode } from 'libphonenumber-js'
import type { SalesOrder } from '@/lib/services/sales-orders/sales-orders.types'
import type {
  FsnFloristCard,
  FsnSalesOrderFulfillmentResponse,
} from '@/lib/services/fsn-network/fsn-network.types'

export type FsnNetworkSendOrder = Pick<SalesOrder, 'id' | 'organization_id'> &
  Partial<Pick<SalesOrder, 'currency_type'>> & {
    fsn_network_order_preview?: Partial<NonNullable<SalesOrder['fsn_network_order_preview']>> | null
  }

export interface FsnNetworkSendDialogProps {
  order: FsnNetworkSendOrder
  open?: boolean
  onOpenChange: (open: boolean) => void
  onSubmitted?: (response: FsnSalesOrderFulfillmentResponse) => void
}

type FsnPhoneFormatter = (phone: string, countryCode?: CountryCode) => string

interface FloristSelectionCardProps {
  florist: FsnFloristCard
  formatPhone?: FsnPhoneFormatter
  isSubmitDisabled?: boolean
  isSubmitting?: boolean
  onSelect: (florist: FsnFloristCard) => void
}

interface FloristSelectionDialogProps {
  open?: boolean
  onOpenChange: (open: boolean) => void
  florists?: FsnFloristCard[]
  isLoading?: boolean
  isError?: boolean
  error?: unknown
  search: string
  onSearchChange: (search: string) => void
  formatPhone?: FsnPhoneFormatter
  onSelect: (florist: FsnFloristCard) => void
  isPending?: boolean
  submittingFloristId?: string | null
  formattedOrderValue?: string | null
}

export { getFsnNetworkOrderEligibility, hasFsnNetworkCardDetails } from './fsn-network-eligibility'

function floristAddressLine(florist: Partial<FsnFloristCard> = {}) {
  return [florist.address, florist.city, florist.state, florist.postal_code]
    .filter((part) => String(part ?? '').trim())
    .join(', ')
}

function floristMatchesSearch(florist: Partial<FsnFloristCard> = {}, search = '') {
  const query = search.trim().toLowerCase()
  if (!query) {
    return true
  }

  return [
    florist.id,
    florist.name,
    florist.phone_number,
    florist.address,
    florist.city,
    florist.state,
    florist.postal_code,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query))
}

function sortFloristCards(left: Partial<FsnFloristCard> = {}, right: Partial<FsnFloristCard> = {}) {
  const leftRank = left.blocked ? 2 : left.preferred ? 0 : 1
  const rightRank = right.blocked ? 2 : right.preferred ? 0 : 1

  if (leftRank !== rightRank) {
    return leftRank - rightRank
  }

  return String(left.name ?? '').localeCompare(String(right.name ?? ''))
}

function FloristSelectionCard({
  florist,
  formatPhone = (value) => value,
  isSubmitDisabled = false,
  isSubmitting = false,
  onSelect,
}: FloristSelectionCardProps) {
  const address = floristAddressLine(florist)
  const phone = florist.phone_number ? formatPhone(florist.phone_number, 'US') : null

  return (
    <div
      className={cn(
        'rounded-xl border border-border/70 bg-background/70 p-3 ring-1 ring-inset ring-transparent',
        florist.blocked
          ? 'bg-muted/35 text-muted-foreground'
          : 'hover:border-primary/50 hover:ring-primary/15 dark:bg-white/[0.03]'
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Typography variant="h5" className="pb-0 break-words">
              {florist.name}
            </Typography>
            {florist.preferred && !florist.blocked ? (
              <Badge variant="warning" size="xs">
                <Star className="size-3 fill-current" />
                Preferred
              </Badge>
            ) : null}
            {florist.blocked ? (
              <Badge variant="muted" size="xs">
                <Ban className="size-3" />
                Blocked
              </Badge>
            ) : null}
          </div>
          {phone ? (
            <Typography variant="supporting" className="flex items-center gap-1.5">
              <Phone className="size-3.5" />
              {phone}
            </Typography>
          ) : null}
          {address ? (
            <Typography variant="supporting" className="block">
              {address}
            </Typography>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          className="w-full shrink-0 sm:w-auto"
          disabled={florist.blocked || isSubmitDisabled}
          onClick={() => onSelect(florist)}
        >
          {isSubmitting ? (
            <Loader2
              aria-hidden="true"
              className="size-4 animate-spin"
              data-testid="selected-florist-submit-spinner"
            />
          ) : null}
          Send to this florist
        </Button>
      </div>
    </div>
  )
}

function FloristSelectionDialog({
  open,
  onOpenChange,
  florists = [],
  isLoading = false,
  isError = false,
  error,
  search,
  onSearchChange,
  formatPhone,
  onSelect,
  isPending = false,
  submittingFloristId = null,
  formattedOrderValue = null,
}: FloristSelectionDialogProps) {
  const visibleFlorists = florists.filter((florist) => floristMatchesSearch(florist, search))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="large" viewport="contained" className="!flex h-[720px] flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>Select filling florist</DialogTitle>
          <DialogDescription>
            Choose a florist or go back and let Flower Shop Network route the order.
          </DialogDescription>
          {formattedOrderValue ? (
            <Typography variant="label" tone="muted">
              FSN order value:{' '}
              <span className="font-semibold text-foreground">{formattedOrderValue}</span>
            </Typography>
          ) : null}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="relative shrink-0">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search florists..."
              className="pl-9"
            />
          </div>

          {isError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Could not load florists</AlertTitle>
              <AlertDescription>{getErrorMessage(error)}</AlertDescription>
            </Alert>
          ) : null}

          {isLoading ? (
            <LoadingState label="Loading florists..." size="spacious" surface="transparent" />
          ) : (
            <ScrollArea className="-mr-3 min-h-0 flex-1 pr-3" viewportClassName="h-full max-h-full">
              <div className="space-y-3">
                {visibleFlorists.map((florist) => (
                  <FloristSelectionCard
                    key={florist.id}
                    florist={florist}
                    formatPhone={formatPhone}
                    isSubmitDisabled={isPending}
                    isSubmitting={isPending && String(florist.id) === submittingFloristId}
                    onSelect={onSelect}
                  />
                ))}
                {visibleFlorists.length === 0 ? (
                  <EmptyState
                    icon={Search}
                    title="No florists found"
                    description="Try a different name, city, or postal code."
                    size="compact"
                  />
                ) : null}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function FsnNetworkSendDialog({
  order,
  open,
  onOpenChange,
  onSubmitted,
}: FsnNetworkSendDialogProps) {
  const [floristPickerOpen, setFloristPickerOpen] = useState(false)
  const [floristSearch, setFloristSearch] = useState('')
  const [submittingFloristId, setSubmittingFloristId] = useState<string | null>(null)
  const { formatPhone } = usePhoneFormatter()
  const submitMutation = useSubmitFsnNetworkSalesOrderMutation()
  const orderValuePreview: Partial<NonNullable<SalesOrder['fsn_network_order_preview']>> =
    order?.fsn_network_order_preview ?? {}
  const previewCurrency = order?.currency_type ?? 'USD'
  const productsAfterDiscount = parseOptionalFsnMoney(orderValuePreview.products_after_discount)
  const previewDelivery = parseOptionalFsnMoney(orderValuePreview.delivery)
  const previewOrderValue = parseOptionalFsnMoney(orderValuePreview.order_value)
  const formattedProductsAfterDiscount =
    productsAfterDiscount !== null ? formatCurrency(productsAfterDiscount, previewCurrency) : null
  const formattedDelivery =
    previewDelivery !== null ? formatCurrency(previewDelivery, previewCurrency) : null
  const formattedOrderValue =
    previewOrderValue !== null ? formatCurrency(previewOrderValue, previewCurrency) : null
  const floristsQuery = useFsnNetworkSalesOrderFlorists({
    organizationId: order?.organization_id,
    salesOrderId: order?.id,
    enabled: open && floristPickerOpen,
  })
  const floristCards = Array.isArray(floristsQuery.data?.data)
    ? [...floristsQuery.data.data].sort(sortFloristCards)
    : []
  const isSubmittingSelectedFlorist = submittingFloristId !== null

  function closeFlow() {
    setFloristPickerOpen(false)
    setFloristSearch('')
    onOpenChange(false)
  }

  async function handleSubmit(florist: FsnFloristCard | null = null) {
    setSubmittingFloristId(florist?.id ? String(florist.id) : null)

    try {
      const response = await submitMutation.mutateAsync({
        salesOrderId: order.id,
        ...(florist?.id
          ? {
              receivingFloristId: florist.id,
              requestedFlorist: florist,
            }
          : {}),
      })
      toast.success('Order sent to Flower Shop Network')
      closeFlow()
      onSubmitted?.(response)
    } catch (error) {
      toast.error('Could not send order to Flower Shop Network', {
        description: getErrorMessage(error),
      })
    } finally {
      setSubmittingFloristId(null)
    }
  }

  function handleOpenFloristPicker() {
    setFloristSearch('')
    setFloristPickerOpen(true)
  }

  return (
    <>
      <Dialog open={open && !floristPickerOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send to Flower Shop Network?</DialogTitle>
            <DialogDescription>
              Send this order now or choose the filling florist before sending.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Flower Shop Network will take over fulfillment</AlertTitle>
            <AlertDescription>
              Flower Shop Network will fill this order and will immediately charge your FSN account.
            </AlertDescription>
          </Alert>
          {formattedProductsAfterDiscount && formattedDelivery && formattedOrderValue ? (
            <div className="space-y-2 rounded-xl border border-border/70 bg-muted/25 px-4 py-3">
              <div className="flex items-center justify-between gap-4">
                <Typography variant="supporting">Products after discount</Typography>
                <Typography variant="label">{formattedProductsAfterDiscount}</Typography>
              </div>
              <div className="flex items-center justify-between gap-4">
                <Typography variant="supporting">Delivery</Typography>
                <Typography variant="label">{formattedDelivery}</Typography>
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-border/70 pt-3">
                <Typography variant="labelStrong">FSN order value</Typography>
                <Typography variant="h3" className="pb-0">
                  {formattedOrderValue}
                </Typography>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeFlow}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleOpenFloristPicker}
              disabled={submitMutation.isPending}
            >
              Select Florist
            </Button>
            <Button
              type="button"
              onClick={() => handleSubmit()}
              disabled={submitMutation.isPending}
            >
              {submitMutation.isPending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FloristSelectionDialog
        open={open && floristPickerOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            closeFlow()
          }
        }}
        florists={floristCards}
        isLoading={floristsQuery.isLoading}
        isError={floristsQuery.isError}
        error={floristsQuery.error}
        search={floristSearch}
        onSearchChange={setFloristSearch}
        formatPhone={formatPhone}
        onSelect={handleSubmit}
        isPending={submitMutation.isPending || isSubmittingSelectedFlorist}
        submittingFloristId={submittingFloristId}
        formattedOrderValue={formattedOrderValue}
      />
    </>
  )
}
